// FlightFields — Presentational field group for the "flight" event type. Renders
// the controlled inputs the source's `renderFlightForm()` produced: Airline,
// Flight Number, Departure/Arrival Airport, Departure/Arrival Date+Time, and the
// optional Confirmation Code, including the per-field required-error display.
//
// Extracted verbatim from `AddEventForm.js` (`renderFlightForm`) as part of the
// behavior-preserving refactor (issue #240). The inputs are fully controlled by
// props: values come from `formData`, edits call `onChange(field, value)` (the
// source's `handleInputChange`), and per-field errors come from `errors` (the
// source's `validationErrors`). The two render helpers — `renderFieldError` and
// `getFieldBorderStyle` — are copied verbatim from the source and read only the
// `errors` prop, so the component stays render-only with NO internal state.
// Labels, placeholders, field order, the `validation-error-<field>` test ids,
// styles, and attributes are identical to the source.
//
// Props:
//   formData - object; the controlled field values
//   errors   - object; per-field validation error messages (keyed by field)
//   onChange - (field, value) => void invoked on each input change
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';

export const FlightFields = React.memo(function FlightFields({
  formData,
  errors,
  onChange
}) {
  // Native date/time inputs have a browser-enforced minimum rendered width
  // (~150px) that multi-column rows can't fit on a phone screen — stack to
  // one column below the mobile breakpoint instead of letting fields
  // overflow the modal.
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const renderFieldError = (field) => {
    if (!errors[field]) return null;
    return (
      <span
        data-testid={`validation-error-${field}`}
        style={{
          display: 'block',
          fontSize: '12px',
          color: '#dc2626',
          marginTop: '4px'
        }}
      >
        {errors[field]}
      </span>
    );
  };

  const getFieldBorderStyle = (field) => ({
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 12px',
    border: `1px solid ${errors[field] ? '#dc2626' : '#d1d5db'}`,
    borderRadius: '6px',
    fontSize: '14px'
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Airline *
          </label>
          <input
            type="text"
            value={formData.airline}
            onChange={(e) => onChange('airline', e.target.value)}
            placeholder="e.g., American Airlines"
            style={getFieldBorderStyle('airline')}
          />
          {renderFieldError('airline')}
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Flight Number *
          </label>
          <input
            type="text"
            value={formData.flightNumber}
            onChange={(e) => onChange('flightNumber', e.target.value)}
            placeholder="e.g., AA1234"
            style={getFieldBorderStyle('flightNumber')}
          />
          {renderFieldError('flightNumber')}
        </div>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Departure Airport *
          </label>
          <input
            type="text"
            value={formData.departureAirport}
            onChange={(e) => onChange('departureAirport', e.target.value)}
            placeholder="e.g., LAX"
            style={getFieldBorderStyle('departureAirport')}
          />
          {renderFieldError('departureAirport')}
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Arrival Airport *
          </label>
          <input
            type="text"
            value={formData.arrivalAirport}
            onChange={(e) => onChange('arrivalAirport', e.target.value)}
            placeholder="e.g., JFK"
            style={getFieldBorderStyle('arrivalAirport')}
          />
          {renderFieldError('arrivalAirport')}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Departure Date *
          </label>
          <input
            type="date"
            value={formData.departureDate}
            onChange={(e) => onChange('departureDate', e.target.value)}
            style={getFieldBorderStyle('departureDate')}
          />
          {renderFieldError('departureDate')}
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Departure Time *
          </label>
          <input
            type="time"
            value={formData.departureTime}
            onChange={(e) => onChange('departureTime', e.target.value)}
            style={getFieldBorderStyle('departureTime')}
          />
          {renderFieldError('departureTime')}
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Arrival Date *
          </label>
          <input
            type="date"
            value={formData.arrivalDate}
            onChange={(e) => onChange('arrivalDate', e.target.value)}
            style={getFieldBorderStyle('arrivalDate')}
          />
          {renderFieldError('arrivalDate')}
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Arrival Time *
          </label>
          <input
            type="time"
            value={formData.arrivalTime}
            onChange={(e) => onChange('arrivalTime', e.target.value)}
            style={getFieldBorderStyle('arrivalTime')}
          />
          {renderFieldError('arrivalTime')}
        </div>
      </div>

      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
          Confirmation Code
        </label>
        <input
          type="text"
          value={formData.confirmationCode}
          onChange={(e) => onChange('confirmationCode', e.target.value)}
          placeholder="e.g., ABC123"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        />
      </div>
    </div>
  );
});

export default FlightFields;
